import React, { useEffect, useState } from "react";
import {
  Typography,
  CircularProgress,
  TextField,
  MenuItem,
  Grid,
  Box,
  Card,
  CardMedia,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Stack,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  collection,
  onSnapshot,
  query,
  where,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
  setDoc,
  deleteField,
} from "firebase/firestore";
import { auth, db, getToken, uploadImage } from "../firebase";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/Search";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import { isLoggedIn } from "../session";
import BottomNav from "./BottomNav";
import EmptyState from "./EmptyState";
import Marcas from "./marcas";
import AddressField from "./AddressField";
import { getImageSrc, handleImgError } from "./CarCard";
import { PageShell, PageHeader, PageContent } from "./Page";

const MyCars = () => {
  const [car, setCar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMarca, setSelectedMarca] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleMarcaChange = (e) => {
    setSelectedMarca(e.target.value);
  };

  const handleDelete = async (carId) => {
    try {
      const carRef = doc(db, "cars", carId);
      await deleteDoc(carRef);
    } catch {
      // Error ignorado intencionalmente
    }
  };

  const handleEditClick = (car) => {
    setSelectedCar(car);
    setOpenModal(true);
  };
  

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedCar(null);
  };

  const handleSaveEdit = async () => {
    if (!selectedCar) return;

    try {
      const propertyRef = doc(db, "cars", selectedCar.id);
      const docSnap = await getDoc(propertyRef);

      if (!docSnap.exists()) {
        return;
      }

      await updateDoc(propertyRef, {
        title: selectedCar.title,
        description: selectedCar.description,
        price: selectedCar.price,
        city: selectedCar.city,
        direccion: selectedCar.direccion || "",
        marca: selectedCar.marca,
        km: selectedCar.km,
        modelo: selectedCar.modelo,
        images: selectedCar.images,
      });
      handleCloseModal();
    } catch {
      // Error ignorado intencionalmente
    }
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedCar((prevCar) => ({
      ...prevCar,
      [name]: value,
    }));
  };

  const handleAddressChange = (e) => {
    const { value, city } = e.target;
    setSelectedCar((prevCar) => ({ ...prevCar, direccion: value, city }));
  };
  

  const formatDate = (timeToDo) => {
    if (timeToDo instanceof Date) {
      return timeToDo.toLocaleDateString();
    } else if (timeToDo && timeToDo.toDate instanceof Function) {
      return timeToDo.toDate().toLocaleDateString();
    }
    return "";
  };

  // Las publicaciones anteriores guardaban DNI y patente en el documento público
  const moveDocumentationToPrivate = (carsData) => {
    carsData
      .filter((item) => item.dni !== undefined || item.patente !== undefined)
      .forEach(async (item) => {
        try {
          await setDoc(
            doc(db, "cars", item.id, "private", "documentacion"),
            { dni: item.dni || "", patente: item.patente || "" },
            { merge: true }
          );
          await updateDoc(doc(db, "cars", item.id), { dni: deleteField(), patente: deleteField() });
        } catch {
          // Se reintenta la próxima vez que se abra la sección
        }
      });
  };

  useEffect(() => {
    const checkAuthAndFetchCars = async () => {
      if (!isLoggedIn()) {
        // Redirect to login page
        return;
      }

      try {
        const token = await getToken();
        if (!token) {
          return;
        }

        const currentUser = auth.currentUser;
        if (!currentUser) {
          return;
        }

        setLoading(true);
        const { uid } = currentUser;
        const q = query(collection(db, "cars"), where("uid", "==", uid));
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const carsData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setCar(carsData);
          setLoading(false);
          moveDocumentationToPrivate(carsData);
        });
        return () => unsubscribe();
      } catch (error) {
        setLoading(false);
      }
    };
    checkAuthAndFetchCars();
  }, []);

  const handleImageUpload = async (event) => {
    const files = event.target.files;

    if (!files || files.length === 0) {
      return;
    }

    setLoading(true);

    try {
      const imageUrls = [];
      for (const file of files) {
        const imageUrl = await uploadImage(file);
        imageUrls.push(imageUrl);
      }

      setSelectedCar((prevCar) => ({
        ...prevCar,
        images: [...(prevCar.images || []), ...imageUrls],
      }));
    } catch (error) {
      alert(error.message || "No se pudieron subir las imágenes.");
    } finally {
      setLoading(false);
    }
  };

  const filteredCars = car.filter((car) => {
    const searchMatch =
      car.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      car.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      car.price.toString().includes(searchTerm) ||
      car.city.toString().includes(searchTerm) ||
      car.modelo.toString().includes(searchTerm) ||
      car.km.toString().includes(searchTerm) ||
      car.marca.toString().toLowerCase().includes(searchTerm.toLowerCase());

    // Ensure selectedMarca is a string and handle cases where it might not be
    const brandMatch =
      (typeof selectedMarca === "string" && selectedMarca === "") ||
      (typeof selectedMarca === "string" &&
        car.marca.toLowerCase().includes(selectedMarca.toLowerCase()));

    return searchMatch && brandMatch;
  });

  const handleRemoveImage = (index) => {
    setSelectedCar((prevFormData) => ({
      ...prevFormData,
      images: prevFormData.images.filter((_, i) => i !== index),
    }));
  };

  return (
    <PageShell>
      <PageHeader
        title="Mis vehículos"
        subtitle="Administrá tus publicaciones"
        actions={
          <Button
            component={Link}
            to="/publish"
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            sx={{ flexShrink: 0 }}
          >
            Publicar
          </Button>
        }
      />

      <PageContent>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 3 }}>
          <TextField
            placeholder="Buscar en mis vehículos..."
            value={searchTerm}
            onChange={handleSearchChange}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label="Marca"
            value={selectedMarca}
            onChange={handleMarcaChange}
            sx={{ minWidth: { sm: 220 } }}
          >
            <MenuItem value="">Todas</MenuItem>
            {Marcas.map((marca) => (
              <MenuItem key={marca} value={marca}>
                {marca}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {loading && !openModal ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress color="secondary" />
          </Box>
        ) : filteredCars.length === 0 ? (
          <EmptyState
            icon={<DirectionsCarOutlinedIcon />}
            title={car.length === 0 ? "Todavía no publicaste vehículos" : "No hay resultados"}
            text={car.length === 0 ? "Publicá tu primer auto y empezá a recibir consultas." : "Probá con otra búsqueda o marca."}
            action={
              car.length === 0 && (
                <Button component={Link} to="/publish" variant="contained" color="secondary" startIcon={<AddIcon />}>
                  Publicar vehículo
                </Button>
              )
            }
          />
        ) : (
          <Grid container spacing={2.5}>
            {filteredCars.map((car) => (
              <Grid item key={car.id} xs={12} sm={6} md={4}>
                <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ position: "relative" }}>
                    <CardMedia
                      component="img"
                      image={getImageSrc(Array.isArray(car.images) ? car.images[0] : car.images)}
                      alt={`Imagen de ${car.title}`}
                      onError={handleImgError}
                      sx={{ aspectRatio: "4 / 3", objectFit: "cover", bgcolor: "background.default" }}
                    />
                    {car.vendido && (
                      <Chip label="Vendido" color="secondary" size="small" sx={{ position: "absolute", top: 12, left: 12 }} />
                    )}
                    {Array.isArray(car.images) && car.images.length > 1 && (
                      <Chip
                        label={`${car.images.length} fotos`}
                        size="small"
                        sx={{ position: "absolute", bottom: 12, right: 12, bgcolor: "rgba(17, 15, 36, 0.7)", color: "#fff" }}
                      />
                    )}
                  </Box>
                  <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em" }}>
                      {[car.marca, car.modelo].filter(Boolean).join(" · ")}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                      {car.title}
                    </Typography>
                    <Typography variant="h6" sx={{ color: "secondary.dark", fontWeight: 800 }}>
                      $ {car.price}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {car.km} km · {car.city}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: "auto", pt: 1 }}>
                      Publicado el {formatDate(car.createdAt)}
                    </Typography>
                  </CardContent>
                  <Stack direction="row" spacing={1} sx={{ px: 2, pb: 2 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => handleEditClick(car)}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      fullWidth
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => handleDelete(car.id)}
                    >
                      Eliminar
                    </Button>
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </PageContent>

      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle>Editar vehículo</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                name="title"
                label="Título"
                fullWidth
                value={selectedCar?.title || ""}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                name="marca"
                label="Marca"
                fullWidth
                value={selectedCar?.marca || ""}
                onChange={handleInputChange}
              >
                {Marcas.map((marca) => (
                  <MenuItem key={marca} value={marca}>
                    {marca}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="modelo"
                label="Modelo"
                fullWidth
                value={selectedCar?.modelo || ""}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="price"
                label="Precio"
                fullWidth
                value={selectedCar?.price || ""}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="km"
                label="Kilómetros"
                fullWidth
                value={selectedCar?.km || ""}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <AddressField
                label="Ubicación del vehículo"
                name="direccion"
                stacked
                value={selectedCar?.direccion || selectedCar?.city || ""}
                onChange={handleAddressChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Descripción"
                fullWidth
                multiline
                rows={4}
                value={selectedCar?.description || ""}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 3, mb: 1.5 }}>
            Fotos
          </Typography>
          <Grid container spacing={1.5}>
            {selectedCar && Array.isArray(selectedCar.images) &&
              selectedCar.images.map((imageUrl, index) => (
                <Grid item xs={4} sm={3} key={index}>
                  <Box sx={{ position: "relative" }}>
                    <Box
                      component="img"
                      src={imageUrl}
                      alt={`Imagen ${index + 1} del vehículo`}
                      sx={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 2, display: "block" }}
                    />
                    <IconButton
                      aria-label="Eliminar imagen"
                      size="small"
                      onClick={() => handleRemoveImage(index)}
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        bgcolor: "rgba(17, 15, 36, 0.7)",
                        color: "#fff",
                        "&:hover": { bgcolor: "rgba(17, 15, 36, 0.9)" },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            <Grid item xs={4} sm={3}>
              <Box
                component="label"
                htmlFor="upload-button"
                sx={{
                  width: "100%",
                  aspectRatio: "1",
                  display: "grid",
                  placeItems: "center",
                  border: 2,
                  borderStyle: "dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "background.default",
                  color: "secondary.dark",
                  cursor: "pointer",
                  "&:hover": { borderColor: "secondary.main" },
                }}
              >
                {loading ? <CircularProgress size={24} color="secondary" /> : <AddPhotoAlternateIcon />}
              </Box>
            </Grid>
          </Grid>
          <input
            type="file"
            accept="image/*"
            onClick={(event) => (event.target.value = null)}
            onChange={handleImageUpload}
            style={{ display: "none" }}
            id="upload-button"
            multiple // Add the multiple attribute
            disabled={loading}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseModal} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" color="secondary">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <BottomNav />
    </PageShell>
  );
};

export default MyCars;
